import { clsx } from "clsx";
import { filter, flattenDeep } from "lodash-es";
import { cloneElement, type CSSProperties, forwardRef, type ReactNode, useEffect, useRef, useState } from "react";
import { FocusScope } from "react-aria";
import { createPortal } from "react-dom";
import Measure from "react-measure";
import { HeaderDivider, HStack, Tag, TagIntent, TriggerButton } from "widgets";
import { useHotKeys } from "widgets/hooks";

import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.js";
import { ProjectColoredBadge } from "@/modules/projects/components/ProjectColoredWidgets.js";
import type { ProjectBasisFragment } from "@/modules/projects/graphql/projects-queries.gql.js";
import { Hotkeys } from "@/settings/hotkeys.js";

import styleCard from "../cards/styles/Card.module.scss";
import styleSkeleton from "../styles/loading-skeleton.module.scss";
import styleAttributes from "./styles/HeaderWidgets.module.scss";
import style from "./styles/PageHeader.module.scss";

// --- Header Component ---
export enum zIntent {
  Default,
  Popover,
}

export interface IPageHeaderProps {
  project?: ProjectBasisFragment;
  tags?: ReactNode[];
  title?: string;
  subtitle?: string;
  titleTrailer?: ReactNode;
  badge?: string;
  widgets?: (ReactNode & { isUiSmall?: boolean })[];
  toolsWidgets?: ReactNode[]; // before the actions
  actionWidgets?: ReactNode[]; // after the tools
  alwaysVisibleActionWidgets?: JSX.Element[]; // after the actionWidgets (like the close icon for Dialogs)
  forceActionWidgetsVisibility?: boolean;
  tipTourId?: string;
  isLoading?: boolean;
  zIntent?: zIntent; // TODO improve or switch to "unset"if possible + CSS
  noSeparator?: boolean;

  // TODO improve
  setDocumentTitle?: boolean;
  tempNoOverflow?: boolean; // TODO remove once the tabs are in NavigationPanel
  tempForceToolsLarge?: boolean;

  // TODO: Remove later once we have the page layout
  newStyle?: boolean;
  style?: CSSProperties;
}

export default function PageHeader(props: IPageHeaderProps): JSX.Element {
  useDocumentTitle(props.title, !props.setDocumentTitle);

  const [headerWidth, setHeaderWidth] = useState(0);
  const [leftFullWidth, setLeftFullWidth] = useState(0);
  const [middleFullWidth, setMiddleFullWidth] = useState(0);
  const [rightFullWidth, setRightFullWidth] = useState(0);

  const [leftResponsiveWidth, setLeftResponsiveWidth] = useState(0);
  const [, setMiddleResponsiveWidth] = useState(0);
  const [, setRightResponsiveWidth] = useState(0);
  const [rightSmallishWidth, setRightSmallishWidth] = useState(0);

  const [isLeftSmall, setLeftSmall] = useState(false);
  const [isMiddleSmall, setMiddleSmall] = useState(false);
  const [isRightSmall, setRightSmall] = useState(false);
  const [isRightSmallish, setRightSmallish] = useState(false);

  useEffect(() => {
    const MARGIN = 42; // + 2px because of float

    // Hidden or not yet rendered
    if (headerWidth === 0) return;

    const level_1 = headerWidth - leftFullWidth - middleFullWidth - rightFullWidth - MARGIN < 0;
    const level_2 = headerWidth - leftResponsiveWidth - middleFullWidth - rightSmallishWidth - MARGIN < 0;

    // All mini
    isMiddleSmall !== level_2 && setMiddleSmall(level_2);
    isRightSmall !== level_2 && setRightSmall(level_2);

    // Actions small +  widgets responsive
    isRightSmallish !== level_1 && setRightSmallish(level_1);
    isLeftSmall !== level_1 && setLeftSmall(level_1);
  }, [
    headerWidth,
    leftFullWidth,
    middleFullWidth,
    rightFullWidth,
    leftResponsiveWidth,
    rightSmallishWidth,
    // middleResponsiveWidth,
    // rightResponsiveWidth,
  ]);

  if (props.isLoading) return <SkeletonHeader noSeparator={props.noSeparator} />;

  // In order to help to make proper calculation for responsive mode (isUiSmall),
  // we need to make some offscreen calculations with never small rendered parts.
  const HeaderPortal = createPortal(
    <div aria-hidden={true} className={style.offscreen}>
      <div className={style.header}>
        <Measure onResize={({ entry }) => setLeftFullWidth(entry?.width || 0)}>
          {({ measureRef }) => <LeftHeader {...props} ref={measureRef} />}
        </Measure>
      </div>
      <div className={style.header}>
        <Measure onResize={({ entry }) => setMiddleFullWidth(entry?.width || 0)}>
          {({ measureRef }) => <MiddleHeader {...props} ref={measureRef} />}
        </Measure>
      </div>
      <div className={style.header}>
        <Measure onResize={({ entry }) => setRightFullWidth(entry?.width || 0)}>
          {({ measureRef }) => <RightHeader {...props} ref={measureRef} renderOffScreen />}
        </Measure>
      </div>
      <div className={style.header}>
        <Measure onResize={({ entry }) => setRightSmallishWidth(entry?.width || 0)}>
          {({ measureRef }) => <RightHeader {...props} isUiSmallish ref={measureRef} renderOffScreen />}
        </Measure>
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      <Measure onResize={({ entry }) => setHeaderWidth(entry?.width || 0)}>
        {({ measureRef }) => (
          <div
            className={clsx(style.header, {
              [style.header_no_overflow]: props.tempNoOverflow,
              [style.header_no_separator]: props.noSeparator,
            })}
            ref={measureRef}
            style={props.newStyle ? { boxShadow: "none", background: "transparent" } : props.style}
          >
            <Measure onResize={({ entry }) => setLeftResponsiveWidth(entry?.width || 0)}>
              {({ measureRef }) => <LeftHeader {...props} isUiSmall={isLeftSmall} ref={measureRef} />}
            </Measure>
            <Measure onResize={({ entry }) => setMiddleResponsiveWidth(entry?.width || 0)}>
              {({ measureRef }) => <MiddleHeader {...props} isUiSmall={isMiddleSmall} ref={measureRef} />}
            </Measure>
            <Measure onResize={({ entry }) => setRightResponsiveWidth(entry?.width || 0)}>
              {({ measureRef }) => (
                <RightHeader
                  {...props}
                  isUiSmall={isRightSmall && props.forceActionWidgetsVisibility !== true}
                  isUiSmallish={isRightSmallish}
                  ref={measureRef}
                />
              )}
            </Measure>
          </div>
        )}
      </Measure>
      {HeaderPortal}
    </>
  );
}

// --- Loading Skeleton ---
function SkeletonHeader({ noSeparator }): JSX.Element {
  return (
    <div className={clsx(style.header, { [style.header_no_separator]: noSeparator })}>
      <div className={style.header_left}>
        <div className={style.header_title_wrapper}>
          <div className={style.title}>
            <span className={styleSkeleton.skeleton}>Lorem ipsum dolor</span>
          </div>
          <div className={style.subtitle}>
            <span className={styleSkeleton.skeleton}>Lorem ipsum dolor sit amet consectetur.</span>
          </div>
        </div>
        <div className={styleAttributes.attributes_list}>
          <SkeletonHeaderAttributeWidget />
          <SkeletonHeaderAttributeWidget />
        </div>
      </div>
      <div className={style.header_right}>
        <div className={style.action_bar}>
          <SkeletonAction />
          <SkeletonAction />
          <SkeletonAction />
        </div>
      </div>
    </div>
  );
}

function SkeletonHeaderAttributeWidget(): JSX.Element {
  return (
    <div className={styleAttributes.attributes_wrapper}>
      <div className={styleAttributes.attributes_title}>
        <span className={styleSkeleton.skeleton}>Lorem</span>
      </div>
      <div className={styleAttributes.attributes_subtitle}>
        <span className={styleSkeleton.skeleton}>Lorem ipsum</span>
      </div>
    </div>
  );
}

function SkeletonAction(): JSX.Element {
  return <div className={styleSkeleton.skeleton} style={{ width: 40, height: 40, margin: 10 }} />;
}

// --- Header Parts ---
const LeftHeader = forwardRef<HTMLDivElement, IPageHeaderProps & { isUiSmall?: boolean }>(
  function LeftHeader(props, ref) {
    let widgets: JSX.Element[] = [];

    if (Array.isArray(props.widgets)) {
      widgets = filter(flattenDeep(props.widgets), (a) => a) as JSX.Element[];
    }

    return (
      <div className={clsx(style.header_left)} ref={ref}>
        {props.tags && props.tags.length > 0 && <div className={styleCard.tags_list}>{props.tags}</div>}
        {props.project && <ProjectColoredBadge project={props.project} />}
        {props.tipTourId && <span id={props.tipTourId} className="hotspot-beacon" />}
        {(props.title || props.subtitle) && (
          <div className={clsx(style.header_title_wrapper, { [style.header_no_margin]: props.titleTrailer })}>
            {props.title && <div className={style.title}>{props.title}</div>}
            {props.subtitle && <div className={style.subtitle}>{props.subtitle}</div>}
          </div>
        )}
        {props.titleTrailer && <div className={style.title_trailer}>{props.titleTrailer}</div>}
        {props.badge && <Tag label={props.badge} intentColor={TagIntent.Outlined} className={style.badge} />}
        {widgets.map((widget) => widget && cloneElement(widget, { isUiSmall: props.isUiSmall }))}
      </div>
    );
  },
);

const MiddleHeader = forwardRef<HTMLDivElement, IPageHeaderProps & { isUiSmall?: boolean }>(function MiddleHeader(
  { isUiSmall, ...props },
  ref,
) {
  let tools: JSX.Element[] = [];

  if (Array.isArray(props.toolsWidgets)) {
    tools = filter(flattenDeep(props.toolsWidgets), (a) => a) as JSX.Element[];
  }

  return (
    <>
      {(props.title || props.subtitle) && tools.length > 0 && <HeaderDivider />}
      <div className={style.header_middle} style={props.tempForceToolsLarge ? { flex: "2 auto" } : {}} ref={ref}>
        {tools.map((tool) => tool && cloneElement(tool, { isUiSmall }))}
      </div>
    </>
  );
});

const RightHeader = forwardRef<
  HTMLDivElement,
  Pick<IPageHeaderProps, "actionWidgets" | "alwaysVisibleActionWidgets" | "zIntent"> & {
    isUiSmall?: boolean;
    isUiSmallish?: boolean;
    renderOffScreen?: boolean;
  }
>(function RightHeader(props, ref) {
  const [isToolsOpen, setToolsOpen] = useState(false);
  const actions = cleanActions(props.actionWidgets, props.renderOffScreen);
  const isSmall = props.isUiSmallish && actions.length > 0;
  const isPopup = props.isUiSmall && actions.length > 0;
  const visibleActions = cleanActions(props.alwaysVisibleActionWidgets, props.renderOffScreen);
  const actionRef = useRef<HTMLDivElement>(null);

  // Just add a divider before the always-visible widgets
  if (visibleActions?.length > 0) {
    visibleActions.unshift(<HeaderDivider key={"page-header-visible-widgets-separator"} />);
  }

  // close the menu one the icons are visible on screen again
  useEffect(() => {
    if (props.isUiSmall === false) {
      setToolsOpen(false);
    }
  }, [props.isUiSmall]);

  useHotKeys(
    Hotkeys.pageHeader.toggleToolsOpen,
    () => {
      if (props.renderOffScreen) return;
      if (isToolsOpen) {
        setToolsOpen(false);
      }
    },
    actionRef,
    { continuePropagation: !isToolsOpen },
  );

  return (
    <div className={style.header_right} ref={ref}>
      <div className={clsx(style.action_bar, { "small-actions": isSmall || isPopup })} ref={actionRef}>
        <div
          className={clsx(style.action_bar, {
            [style.action_bar_popup]: isPopup,
            [style.action_bar_visible]: isToolsOpen,
          })}
        >
          {/*  FIXME keep contain={false} to keep focus on not-"react-aria" dialogs */}
          <FocusScope autoFocus={isToolsOpen} restoreFocus contain={false}>
            <HStack spacing={1}>{actions}</HStack>
          </FocusScope>
        </div>
        {isPopup && (
          <TriggerButton
            icon={isToolsOpen ? "keyboard_double_arrow_right" : "keyboard_double_arrow_left"}
            onPress={() => setToolsOpen(!isToolsOpen)}
            label={"Tools"}
            showLabel
          />
        )}
        {visibleActions}
      </div>
    </div>
  );
});

// --- Utilities ---
export function cleanActions(widgets?: ReactNode[], renderOffScreen?: boolean): JSX.Element[] {
  let actions: JSX.Element[] = [];

  if (Array.isArray(widgets)) {
    // Remove undefined, null, false, O, "" and all other falsy values
    actions = filter(flattenDeep(widgets), (w) => !!w) as JSX.Element[];
    actions = actions.map((action) => cloneElement(action, { renderOffScreen }));
  }

  return actions;
}
